/**
 * @fileoverview Multi-value selection in the shared design system. Callers own options, selected values and persistence; this control owns filtering, keyboard dismissal and the temporary option surface. It emits the same value envelope as AppDropdown.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import AppButton from './AppButton';
import AppIcon from './AppIcon';
import { Checkbox } from './AppCheckbox';
import './AppControls.css';

export function MultiSelect({ value = [], options = [], onChange, optionLabel, optionValue, optionDisabled,
  inputId, id, placeholder = 'Select', disabled, filter, className = '', style, maxSelectedLabels = 3,
  selectedItemsLabel, display, showClear, showSelectAll = true, itemTemplate, panelHeaderTemplate,
  optionGroupLabel, optionGroupChildren, scrollHeight, appendTo, ...props }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const root = useRef(null);
  const listId = useId();
  const flat = optionGroupChildren ? options.flatMap(group => group[optionGroupChildren] || []) : options;
  const choices = flat.map(option => ({ option, label: String(optionLabel ? option[optionLabel] : option.label ?? option.name ?? option),
    value: optionValue ? option[optionValue] : option.value ?? option,
    disabled: typeof optionDisabled === 'function' ? optionDisabled(option) : optionDisabled ? option[optionDisabled] : option.disabled }));
  const selected = value || [];
  const filtered = choices.filter(option => option.label.toLowerCase().includes(query.toLowerCase()));
  const labels = choices.filter(option => selected.includes(option.value)).map(option => option.label);
  const change = next => onChange?.({ value: next, target: { id: inputId || id, value: next } });

  useEffect(() => {
    if (!open) return;
    const outside = event => { if (!root.current?.contains(event.target)) setOpen(false); };
    document.addEventListener('mousedown', outside);
    return () => document.removeEventListener('mousedown', outside);
  }, [open]);

  return <div ref={root} className={`app-multiselect ${className}`} style={style}
    onKeyDown={event => { if (event.key === 'Escape') { setOpen(false); root.current?.querySelector('button')?.focus(); } }}>
    <button type="button" id={inputId || id} role="combobox" aria-expanded={open} aria-controls={listId}
      aria-label={props['aria-label']} aria-labelledby={props['aria-labelledby']} disabled={disabled}
      className="app-choice-trigger" onClick={() => setOpen(!open)}>
      <span>{labels.length ? labels.length > maxSelectedLabels ? (selectedItemsLabel || '{0} selected').replace('{0}', labels.length) : labels.join(', ') : placeholder}</span>
      <AppIcon icon="pi pi-chevron-down" />
    </button>
    {showClear && selected.length > 0 && <AppButton icon="pi pi-times" aria-label="Clear selection" text disabled={disabled} onClick={() => change([])} />}
    {open && <div className="app-choice-panel">
      {panelHeaderTemplate && (typeof panelHeaderTemplate === 'function' ? panelHeaderTemplate({}) : panelHeaderTemplate)}
      {filter && <input className="app-choice-search" aria-label="Filter options" value={query} onChange={event => setQuery(event.target.value)} />}
      {showSelectAll && filtered.length > 0 && <label className="app-choice-option">
        <Checkbox checked={filtered.every(option => selected.includes(option.value))}
          onChange={event => { const enabled = filtered.filter(option => !option.disabled).map(option => option.value);
            change(event.checked ? [...new Set([...selected, ...enabled])] : selected.filter(item => !enabled.includes(item))); }} />Select all
      </label>}
      <div id={listId} role="listbox" aria-multiselectable="true" style={{ maxHeight: scrollHeight || 260, overflowY: 'auto' }}>
        {filtered.map((option, index) => <label key={index} role="option" aria-selected={selected.includes(option.value)} className="app-choice-option">
          <Checkbox checked={selected.includes(option.value)} disabled={option.disabled} onChange={event =>
            change(event.checked ? [...selected, option.value] : selected.filter(item => item !== option.value))} />
          {itemTemplate ? itemTemplate(option.option) : option.label}
        </label>)}
        {!filtered.length && <p>No results</p>}
      </div>
    </div>}
  </div>;
}

export default MultiSelect;
